import React, { useEffect, useMemo } from 'react';
import {
  Activity,
  Briefcase,
  Eye,
  Layers,
  PlusCircle,
  Scale,
  Shield,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import HoldingsScreen from '../HoldingsScreen';
import PortfolioConstructionScreen from '../PortfolioConstructionScreen';
import LoanAuctionWorkbench from '../construction/LoanAuctionWorkbench';
import PortfolioRebalancingScreen from '../PortfolioRebalancingScreen';
import PortfolioHedgingScreen from '../PortfolioHedgingScreen';
import CreditLoansTab from '../CreditLoansTab';
import AlternativesTab from '../AlternativesTab';
import PortfolioHubLayout, { PortfolioHubTab } from '../portfolio/PortfolioHubLayout';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

type TabType =
  | 'holdings'
  | 'construction'
  | 'rebalancing'
  | 'hedging'
  | 'credit-loans'
  | 'alternatives';

const DEFAULT_TAB: TabType = 'holdings';

const InstitutionalPortfolioHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isSidecarMode,
    hasSidecarFeatures,
    externalPlatformName,
    hasAIPortfolioConstruction,
    hasAIRebalancer,
    hasAIHedging,
  } = useFeatureFlags();

  const tabs = useMemo<PortfolioHubTab[]>(() => {
    return [
      { id: 'holdings', label: 'Holdings', icon: <Eye className="w-4 h-4" />, badge: 'View' },
      ...(hasAIPortfolioConstruction || !isSidecarMode
        ? [{ id: 'construction', label: 'Construction', icon: <PlusCircle className="w-4 h-4" />, badge: 'Build' }]
        : []),
      ...(hasAIRebalancer || !isSidecarMode
        ? [{ id: 'rebalancing', label: 'Rebalancing', icon: <Scale className="w-4 h-4" />, badge: 'Optimize' }]
        : []),
      ...(hasAIHedging || !isSidecarMode
        ? [{ id: 'hedging', label: 'Hedging', icon: <Shield className="w-4 h-4" />, badge: 'Protect' }]
        : []),
      { id: 'credit-loans', label: 'Credit & Loans', icon: <Briefcase className="w-4 h-4" />, badge: 'Credit' },
      { id: 'alternatives', label: 'Alternatives', icon: <Layers className="w-4 h-4" />, badge: 'Privates' },
    ];
  }, [
    hasAIHedging,
    hasAIPortfolioConstruction,
    hasAIRebalancer,
    isSidecarMode,
  ]);

  const validTabs = new Set(tabs.map(tab => tab.id as TabType));
  const requestedTab = searchParams.get('tab') as TabType | null;
  const activeTab = requestedTab && validTabs.has(requestedTab) ? requestedTab : DEFAULT_TAB;

  useEffect(() => {
    if (searchParams.get('lane') === 'clo-allocation') setSearchParams({ tab: 'construction', lane: 'builder', clo: '1' }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (requestedTab !== activeTab) {
      // F-PCP-019: correct the tab without dropping the sub-tab and view a deep link carries.
      const next = new URLSearchParams(searchParams); next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, requestedTab, searchParams, setSearchParams]);

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  return (
    <PortfolioHubLayout
      title="Portfolio Management"
      description="Institutional workflow for construction, rebalancing, hedging, credit, and alternatives with one consistent operating model."
      segmentLabel={isSidecarMode && hasSidecarFeatures ? `${externalPlatformName} Connected` : 'Institutional'}
      segmentTone={isSidecarMode && hasSidecarFeatures ? 'violet' : 'blue'}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      headerAside={
        <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900/60 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
          <Activity className="w-4 h-4 text-blue-400" />
          Production Workspace
        </div>
      }
    >
      {activeTab === 'holdings' && <HoldingsScreen />}
      {activeTab === 'construction' && (
        <div>
          {/* Specialized construction workflows share the portfolio management navigation. */}
          <div className="flex flex-wrap items-center gap-1.5 mb-4">
            {([
              { id: 'builder', label: 'Portfolio Builder' },
              { id: 'auctions', label: 'Loan Auctions · BWIC/OWIC' },
            ] as const).map(lane => {
              const activeLane = searchParams.get('lane') === 'auctions' ? 'auctions' : 'builder';
              const active = activeLane === lane.id;
              return (
                <button
                  key={lane.id}
                  onClick={() => setSearchParams({ tab: 'construction', lane: lane.id }, { replace: true })}
                  className={`px-3.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${active
                    ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-200'
                    : 'bg-slate-900/60 border-white/10 text-slate-400 hover:text-slate-200 hover:bg-white/5'}`}
                >
                  {lane.label}
                </button>
              );
            })}
          </div>
          {/* The CLO Loan Allocation workbench was retired into the builder (2026-10); its old links land on the managed-CLO lane. */}
          {searchParams.get('lane') === 'auctions' ? <LoanAuctionWorkbench /> : <PortfolioConstructionScreen key={searchParams.get('clo') || 'builder'} />}
        </div>
      )}
      {activeTab === 'rebalancing' && <PortfolioRebalancingScreen />}
      {activeTab === 'hedging' && <PortfolioHedgingScreen />}
      {activeTab === 'credit-loans' && <CreditLoansTab />}
      {activeTab === 'alternatives' && <AlternativesTab />}
    </PortfolioHubLayout>
  );
};

export default InstitutionalPortfolioHub;
